import React, { useEffect } from "react";
import { Modal, Form, Input, Button, InputNumber } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import {
  useCreateLocation,
  useUpdateLocation,
  Location,
} from "../../hooks/useLocation";

interface AddLocationModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  location?: Location | null; // For editing existing location
}

const AddLocationModal: React.FC<AddLocationModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  location,
}) => {
  const [form] = Form.useForm();
  const createLocationMutation = useCreateLocation();
  const updateLocationMutation = useUpdateLocation();

  const isEditing = !!location;

  // Reset form when modal opens/closes or location changes
  useEffect(() => {
    if (visible && location) {
      // Editing mode - populate form with location data
      form.setFieldsValue({
        name: location.name,
        address: location.address,
        code: location.code,
        minimum_order_amount: location.minimum_order_amount,
      });
    } else if (visible) {
      // Adding mode - reset form
      form.resetFields();
    }
  }, [visible, location, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (isEditing && location) {
        // Update existing location
        updateLocationMutation.mutate(
          {
            locationId: location.id,
            locationData: {
              name: values.name.trim(),
              address: values.address.trim(),
              code: values.code.trim().toUpperCase(),
              minimum_order_amount: values.minimum_order_amount,
            },
          },
          {
            onSuccess: () => {
              form.resetFields();
              onSubmit();
            },
          }
        );
      } else {
        // Create new location
        createLocationMutation.mutate(
          {
            name: values.name.trim(),
            address: values.address.trim(),
            code: values.code.trim().toUpperCase(),
            minimum_order_amount: values.minimum_order_amount,
          },
          {
            onSuccess: () => {
              form.resetFields();
              onSubmit();
            },
          }
        );
      }
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <EnvironmentOutlined className="text-primary" />
          <span>{isEditing ? "Edit Location" : "Add New Location"}</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={
            createLocationMutation.isPending || updateLocationMutation.isPending
          }
          onClick={handleSubmit}
        >
          {isEditing ? "Update Location" : "Create Location"}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          label="Location Name"
          name="name"
          rules={[
            { required: true, message: "Please enter the location name" },
            { min: 2, message: "Location name must be at least 2 characters" },
            { max: 100, message: "Location name cannot exceed 100 characters" },
          ]}
        >
          <Input placeholder="e.g., Lagos Main Branch" maxLength={100} />
        </Form.Item>

        <Form.Item
          label="Address"
          name="address"
          rules={[
            { required: true, message: "Please enter the location address" },
            { min: 5, message: "Address must be at least 5 characters" },
            { max: 200, message: "Address cannot exceed 200 characters" },
          ]}
        >
          <Input.TextArea
            placeholder="e.g., 123 Main Street, Victoria Island"
            rows={3}
            maxLength={200}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="Location Code"
          name="code"
          rules={[
            { required: true, message: "Please enter the location code" },
            {
              pattern: /^[A-Z]{2,5}$/,
              message: "Code must be 2-5 uppercase letters only",
            },
          ]}
          tooltip="A unique 2-5 letter code to identify this location (e.g., LG for Lagos)"
        >
          <Input
            placeholder="e.g., LG"
            maxLength={5}
            style={{ textTransform: "uppercase" }}
            onChange={(e) => {
              // Auto-convert to uppercase
              const upperValue = e.target.value.toUpperCase();
              form.setFieldValue("code", upperValue);
            }}
          />
        </Form.Item>

        <Form.Item
          label="Minimum Order Amount"
          name="minimum_order_amount"
          rules={[
            {
              required: true,
              message: "Please enter the minimum order amount",
            },
            { type: "number", min: 0, message: "Amount must be 0 or greater" },
          ]}
          tooltip="The minimum order amount required for this location"
        >
          <InputNumber
            placeholder="e.g., 5000"
            style={{ width: "100%" }}
            min={0}
            precision={2}
            formatter={(value) =>
              `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            // parser={(value) => value!.replace(/₦\s?|(,*)/g, "")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddLocationModal;
